import { useTranslation } from 'react-i18next';
/**
 * Full-screen photo viewer for the public QR page.
 *
 * A fitter on site taps a thumbnail and wants to see the whole plate or
 * the whole damage, not a thumbnail. Deliberately dependency-free: this
 * page is unauthenticated and must stay light on a phone signal.
 *
 * Closes on backdrop tap, the X, or Escape. Arrow keys and a horizontal
 * swipe move between photos. Body scroll is locked while open so the
 * page behind doesn't drift under the overlay.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export const PhotoLightbox = ({ photos, index, onClose, onIndexChange, alt }) => {
  const { t } = useTranslation();
  const touchStartX = useRef(null);
  const count = photos.length;

  const step = useCallback((delta) => {
    onIndexChange((index + delta + count) % count);
  }, [index, count, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && count > 1) step(1);
      else if (e.key === 'ArrowLeft' && count > 1) step(-1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, step, count]);

  const onTouchEnd = (e) => {
    if (touchStartX.current === null || count < 2) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black p-4 animate-in fade-in duration-150"
      onClick={onClose}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      data-testid="public-photo-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 z-10 rounded-full bg-white/15 p-2 text-white transition-colors hover:bg-white/30"
        aria-label={t('auto.misc2.close')}
        data-testid="lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-3 z-10 rounded-full bg-white/15 p-2 text-white transition-colors hover:bg-white/30"
            aria-label={t('auto.misc2.previousPhoto')}
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-3 z-10 rounded-full bg-white/15 p-2 text-white transition-colors hover:bg-white/30"
            aria-label={t('auto.misc2.nextPhoto')}
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white"
            data-testid="lightbox-counter"
          >
            {index + 1} / {count}
          </div>
        </>
      )}

      <img
        src={photos[index]}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-full object-contain select-none"
        data-testid="lightbox-image"
      />
    </div>
  );
};

export default PhotoLightbox;
